import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type RowDrawerDiscardDialogProps = {
    isOpen: boolean;
    rowNumber: number;
    onKeepEditing: () => void;
    onDiscard: () => void;
};

/**
 * Asked when the row drawer is dismissed with unsaved edits. The shape of
 * the import's leave dialog (`CatalogImportLeaveGuard`): Keep editing is
 * the safe answer, and dismissing the dialog (Escape, the backdrop, its
 * close button) is Keep editing too.
 */
export const RowDrawerDiscardDialog: React.FC<RowDrawerDiscardDialogProps> = ({
    isOpen,
    rowNumber,
    onKeepEditing,
    onDiscard,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Modal
            isOpen={isOpen}
            onOpenChange={(open) => !open && onKeepEditing()}
        >
            <Modal.Backdrop>
                <Modal.Container size="md">
                    <Modal.Dialog
                        className="w-full max-w-lg"
                        {...getNodeTestIdProp(
                            'catalog-import-row-drawer-discard',
                        )}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading>
                                {t(
                                    'catalogImport.products.drawer.discard.title',
                                    { row: rowNumber },
                                )}
                            </Modal.Heading>
                        </Modal.Header>
                        <Modal.Body>
                            <p className="text-sm text-muted">
                                {t(
                                    'catalogImport.products.drawer.discard.body',
                                )}
                            </p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                onPress={onKeepEditing}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'catalog-import-row-drawer-discard-keep',
                                )}
                            >
                                {t(
                                    'catalogImport.products.drawer.discard.keep',
                                )}
                            </Button>
                            <Button
                                onPress={onDiscard}
                                variant="danger"
                                {...getNodeTestIdProp(
                                    'catalog-import-row-drawer-discard-confirm',
                                )}
                            >
                                {t(
                                    'catalogImport.products.drawer.discard.discard',
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
